<template>
    <asli-form @submitted="goNext">
        <asli-form-section title="Your Details" heading-level="h2">
            <asli-radio
                id="title"
                name="title"
                rules="required:title"
                v-model="personalDetails.title"
                autocomplete="honorific-prefix"
                label="Title"
                :options="lists?.title"
            />
            <form-transition :become-visible-when="!!personalDetails.title" @show="commitRisk">
                <asli-text-input
                    id="first_name"
                    name="first_name"
                    rules="required:first_name"
                    v-model="personalDetails.first_name"
                    autocomplete="given-name"
                    label="First Name"
                    :mask="NAME_MASK_ANPOST"
                    large
                    is-capitalized
                />
            </form-transition>
            <form-transition :become-visible-when="!!personalDetails.first_name" @show="commitRisk">
                <asli-text-input
                    id="surname"
                    name="surname"
                    rules="required:last_name"
                    v-model="personalDetails.surname"
                    label="Last Name"
                    autocomplete="family-name"
                    :mask="NAME_MASK_ANPOST"
                    large
                    is-capitalized
                />
            </form-transition>
            <form-transition :become-visible-when="!!personalDetails.surname" @show="commitRisk">
                <asli-date
                    id="date_of_birth"
                    name="date_of_birth"
                    rules="required:date_of_birth"
                    label="Date of Birth"
                    v-model="personalDetails.date_of_birth"
                    autocomplete="on"
                    initial-mode="year"
                    :date-from="{ date: oldestAge, message: oldestAgeValidationMessage }"
                    :date-to="{ date: youngestAge, message: youngestAgeValidationMessage }"
                    :show-today="false"
                    calendar-aria-label="Open calendar to select date of birth"
                />
            </form-transition>
            <form-transition :become-visible-when="!!personalDetails.date_of_birth" @show="commitRisk">
                <asli-text-input
                    id="mobile_phone"
                    name="mobile_phone"
                    label="Mobile Number"
                    rules="required:mobile_phone|phone-number-anpost"
                    autocomplete="tel"
                    v-model="proposer.mobile_phone_number"
                    inputmode="tel"
                    large
                    :mask="PHONE_NUMBER_MASK"
                    :tooltipText="YOUR_DETAILS_HELP_TEXT.mobile_phone"
                />
            </form-transition>
            <form-transition :become-visible-when="!!proposer.mobile_phone_number" @show="commitRisk">
                <asli-text-input
                    id="email"
                    name="email"
                    label="Email"
                    large
                    rules="required:email|email:true"
                    v-model="proposerEmail"
                    inputmode="email"
                    autocomplete="email"
                    :tooltipText="YOUR_DETAILS_HELP_TEXT.email"
                    max-length="50"
                />
            </form-transition>
        </asli-form-section>
        <form-transition :become-visible-when="!!proposer.email" @show="commitRisk">
            <asli-form-section title="Employment Details" heading-level="h2">
                <asli-search
                    id="occupation"
                    v-model="employmentDetails.occupation"
                    label="What is your occupation?"
                    :options="lists?.occupation"
                    placeholder="Begin typing..."
                    :rules="{ 'required-occupation': true, 'in-list-occupation': lists?.occupation }"
                    description="Type and select an occupation option from the list provided."
                    :tooltipText="YOUR_DETAILS_HELP_TEXT.occupation"
                    show-dropdown-on-focus
                    @focus="showFormButtons = true"
                    @focus-out="showFormButtons = false"
                />
            </asli-form-section>
        </form-transition>
        <div v-if="!formIsEntirelyVisible" class="h-0">
            <button
                class="pointer-events-none bottom-0 opacity-0 h-0"
                type="button"
                @click="
                    () => {
                        return;
                    }
                "
            >
                element to keep focus in window
            </button>
        </div>
        <div v-show="formIsEntirelyVisible || showFormButtons">
            <form-buttons :show-continue="formIsEntirelyVisible || showFormButtons" :show-back="false" />
        </div>
    </asli-form>
</template>

<script lang="ts" setup>
import {
    AsliForm,
    AsliFormSection,
    AsliRadio,
    AsliTextInput,
    AsliDate,
    AsliSearch,
    getDateMinusYears,
    NAME_MASK_ANPOST
} from "@mbs-npm/anpost-ui";
import FormButtons from "@/components/FormButtons.vue";
import { useRouter } from "vue-router";
import { ROUTE_NAMES } from "@/router/types";
import { useMainStore } from "@/stores/main";
import { EmploymentDetails, PersonalDetails, Proposer } from "@/stores/risk/types/drivers/driver-types";
import { useProposalStore } from "@/stores/risk/risk";
import { computed, reactive, ref } from "vue";
import { useListStore } from "@/stores/lists/lists";
import { PHONE_NUMBER_MASK } from "@/static/masks";
import { getResolvedRouteName } from "@/router/utils";
import { YOUR_DETAILS_HELP_TEXT } from "@/utils/helpText";
import { useMainPersistentStore } from "@/stores/mainPersistent";
import FormTransition from "@/components/FormTransitions/FormTransition.vue";
import { capitalizeName } from "@/utils";
import { APP_STEP } from "@/stores/types";

const mainStore = useMainStore();
const mainStorePersistent = useMainPersistentStore();
const listStore = useListStore();
const lists = computed(() => listStore?.lists);
const proposalStore = useProposalStore();
const storedRisk = proposalStore.proposal.risk;

mainStore.setAppStep(APP_STEP.ABOUT_YOU);
const router = useRouter();

const personalDetails = reactive<Partial<PersonalDetails>>({
    title: storedRisk?.proposer?.personal_details?.title,
    first_name: storedRisk?.proposer?.personal_details?.first_name,
    surname: storedRisk?.proposer?.personal_details?.surname,
    date_of_birth: storedRisk?.proposer?.personal_details?.date_of_birth
});

const proposer = reactive<Partial<Proposer>>({
    mobile_phone_number: storedRisk?.proposer?.mobile_phone_number,
    email: storedRisk?.proposer?.email
});

const proposerEmail = computed({
    get: () => proposer.email,
    set: value => (proposer.email = value?.trim() || "")
});

const employmentDetails = reactive<Partial<EmploymentDetails>>({
    occupation: storedRisk?.proposer?.employment_details?.occupation
});

function commitRisk() {
    proposalStore.patchProposer(proposer);
    proposalStore.patchPersonalDetails({
        ...personalDetails,
        first_name: capitalizeName(personalDetails.first_name ?? ""),
        surname: capitalizeName(personalDetails.surname ?? "")
    });
    proposalStore.patchEmploymentDetails(employmentDetails);
}

function goNext() {
    commitRisk();
    mainStorePersistent.setGuardStep(APP_STEP.YOUR_PROPERTY);
    router.push({ name: getResolvedRouteName(ROUTE_NAMES.YourProperty, router.currentRoute.value) });
}

const oldestAge = computed(() => getDateMinusYears(100));
const youngestAge = computed(() => getDateMinusYears(18));

const oldestAgeValidationMessage = "Applicant must be under 100 years of age.";
const youngestAgeValidationMessage = "Applicant must be at least 18 years of age.";

const formIsEntirelyVisible = computed(() => !!employmentDetails.occupation);

const showFormButtons = ref(false);
</script>
